import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Sprout, Loader2, HelpCircle } from "lucide-react";
import HelpGuide from "@/components/HelpGuide";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset | twofa
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (user && user.id) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (params.get("token")) { setMode("reset"); setForm((f) => ({ ...f, password: "" })); }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(form.email, form.password);
        if (res?.twofa_required) { setMode("twofa"); setCode(""); }
        else navigate("/");
      }
      else if (mode === "twofa") {
        const res = await login(form.email, form.password, code.trim());
        if (res?.twofa_required) setError("Enter the 6-digit code from your authenticator app.");
        else navigate("/");
      }
      else if (mode === "register") { await register(form.name, form.email, form.password); navigate("/"); }
      else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setNotice(data.message || "If that email is registered, a reset link has been generated.");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: params.get("token"), password: form.password });
        setNotice("Password updated. You can now sign in.");
        setMode("login");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const HEADINGS = {
    login: ["Welcome back", "Sign in to your classroom."],
    register: ["Create your account", "Set up your teacher account."],
    forgot: ["Reset your password", "We'll generate a secure reset link for your administrator."],
    reset: ["Choose a new password", "Enter a new password for your account."],
    twofa: ["Two-factor verification", "Enter the 6-digit code from your authenticator app."],
  };
  const submitLabel = { login: "Sign in", register: "Create account", forgot: "Send reset link", reset: "Update password", twofa: "Verify & sign in" }[mode];

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#4A5D6B] text-white relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full" style={{ background: "radial-gradient(circle,#81B29A55,transparent 70%)" }} />
        <div className="absolute -left-20 bottom-10 h-72 w-72 rounded-full" style={{ background: "radial-gradient(circle,#F6C35833,transparent 70%)" }} />
        <div className="flex items-center gap-2.5 relative">
          <span className="h-10 w-10 rounded-2xl bg-[#81B29A] flex items-center justify-center"><Sprout size={22} /></span>
          <span className="font-['Outfit'] font-bold text-xl">BloomTrack</span>
        </div>
        <div className="relative">
          <h1 className="font-['Outfit'] text-4xl xl:text-5xl font-bold leading-tight tracking-tight">Map, track &amp; grow<br />every child's progress.</h1>
          <p className="mt-5 text-white/70 text-lg max-w-md leading-relaxed font-['Nunito']">A calm, simple tool for Irish junior classrooms — set priority learning targets and assess literacy, numeracy and social-emotional skills against the curriculum.</p>
        </div>
        <div className="relative flex gap-3 text-sm text-white/60 font-['Outfit']">
          {["Literacy", "Numeracy", "Social & Emotional"].map((t) => <span key={t} className="px-3 py-1 rounded-full bg-white/10">{t}</span>)}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F9F9F7]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <span className="h-10 w-10 rounded-2xl bg-[#81B29A] flex items-center justify-center text-white"><Sprout size={22} /></span>
            <span className="font-['Outfit'] font-bold text-xl text-slate-800">BloomTrack</span>
          </div>

          <h2 className="font-['Outfit'] text-3xl font-bold text-slate-800">{HEADINGS[mode][0]}</h2>
          <p className="text-slate-500 mt-2 mb-7">{HEADINGS[mode][1]}</p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-sm font-semibold text-slate-600 font-['Outfit']">Full name</label>
                <input data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] outline-none transition" placeholder="Ms. Murphy" />
              </div>
            )}
            {mode !== "reset" && mode !== "twofa" && (
              <div>
                <label className="text-sm font-semibold text-slate-600 font-['Outfit']">Email</label>
                <input data-testid="email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] outline-none transition" placeholder="you@school.ie" />
              </div>
            )}
            {mode !== "forgot" && mode !== "twofa" && (
              <div>
                <label className="text-sm font-semibold text-slate-600 font-['Outfit']">Password</label>
                <input data-testid="password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] outline-none transition" placeholder="••••••••" />
                {(mode === "register" || mode === "reset") && (
                  <p className="text-xs text-slate-400 mt-1.5">At least 8 characters, including a letter and a number.</p>
                )}
                {mode === "login" && (
                  <button type="button" data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                    className="text-xs text-[#4A5D6B] font-semibold hover:underline mt-1.5">Forgot password?</button>
                )}
              </div>
            )}

            {mode === "twofa" && (
              <div>
                <label className="text-sm font-semibold text-slate-600 font-['Outfit']">Authentication code</label>
                <input data-testid="twofa-code-input" autoFocus required value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10))}
                  className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl px-4 py-3 tracking-[0.3em] text-center text-lg focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] outline-none transition" placeholder="123456" />
                <p className="text-xs text-slate-400 mt-1.5">6-digit code from your authenticator app, or an 8-character backup code (e.g. 1A2B3C4D).</p>
                <button type="button" data-testid="twofa-back-btn" onClick={() => { setMode("login"); setCode(""); setError(""); }}
                  className="text-xs text-[#4A5D6B] font-semibold hover:underline mt-1.5">Back to sign in</button>
              </div>
            )}

            {notice && <p data-testid="auth-notice" className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-4 py-2.5">{notice}</p>}
            {error && <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2.5">{error}</p>}

            <button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] transition shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 size={18} className="animate-spin" />}{submitLabel}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setNotice(""); }}
              className="text-[#4A5D6B] font-semibold hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          <button
            type="button"
            data-testid="login-help-btn"
            onClick={() => setHelpOpen(true)}
            className="mx-auto mt-5 flex items-center gap-1.5 text-sm font-semibold font-['Outfit'] text-[#5d8a73] hover:text-[#3f6b54] transition"
          >
            <HelpCircle size={16} /> New to BloomTrack? See how it works
          </button>
        </div>
      </div>
      {helpOpen && <HelpGuide onClose={() => setHelpOpen(false)} />}
    </div>
  );
}
